import { resolveDestination, resolveTheme, validDestinations } from '@/wabunka-src/common/utils/url/legacyTagRedirect';
import { ABOUT_LP_URL } from '@/wabunka-src/constants/aboutLp';

/**
 * URL の正規形は「末尾スラッシュあり」（ADR-146）。
 *
 * 301 を 1 箇所に集約しているのは**チェーンを作らない**ため。旧URL解決と末尾スラッシュ付与を
 * 別レイヤー（route middleware と server middleware）に分けると、`/categories/tea` のような
 * 「なし形かつ旧URL」が `なし形 → あり形 → 新URL` の 2 ホップになる。
 * ここで最終形まで解決してから 1 回だけ 301 する。
 */

/** 拡張子付きの最終セグメントは静的ファイル・`sitemap.xml` 等なので正規化しない */
const hasFileExtension = (pathname: string): boolean => /\.[^./]+$/.test(pathname);

/** `//evil.com` `/\evil.com` は Location に載せると外部ホストへ解決する（WHATWG URL は `\` を `/` と見なす） */
const isProtocolRelative = (pathname: string): boolean => pathname.startsWith('//') || pathname.startsWith('/\\');

/** 楽天アフィリエイトの着地点。外部から `/rakuten?ranSiteID=...&url=...` で入るので、301 を挟まず本来の遷移だけにする */
const isAffiliateGateway = (pathname: string): boolean => pathname === '/rakuten';

/**
 * `/api/**` は Nitro の API ルート（`/api/youtubeOembed` 等）で、末尾スラッシュを付けると解決しない。
 * `/_` 始まりは `_nuxt` / `_ipx` などフレームワークの内部パス。
 */
const isNormalizationTarget = (pathname: string): boolean => {
  return (
    pathname.startsWith('/') &&
    !isProtocolRelative(pathname) &&
    !isAffiliateGateway(pathname) &&
    !pathname.startsWith('/api/') &&
    !pathname.startsWith('/_') &&
    !hasFileExtension(pathname)
  );
};

interface LegacyRedirect {
  /** 遷移先。サイト内は末尾スラッシュあり形の絶対パス、サイト外は絶対 URL */
  path: string;
  /** 元のクエリを引き継ぐか */
  keepQuery: boolean;
  /** 引き継いだクエリに追加・上書きするパラメータ */
  query?: Record<string, string>;
}

const EXPERIENCE_SEARCH_PATH = '/s/experiences/';

// マーケの 301 ワークリスト（wabunka_301_phased_worklist_20260904）の P1「即時301（受け皿あり）」。/areas/kamakura-hakone は本文移植が条件なので含めない
const RETIRED_PAGE_REDIRECTS = new Map<string, string>([
  ['/themes/_sushi', '/japan/sushi/'],
  ['/themes/sushi', '/japan/sushi/'],
  ['/themes/bonsai', '/japan/bonsai/'],
  ['/themes/chopstick', '/japan/chopstick/'],
  ['/themes/confectionery', '/japan/wagashi-sweets/'],
  ['/themes/craft_kansai', '/kyoto/'],
  ['/themes/craft_kanto', '/tokyo/'],
  ['/themes/experiential-stays', '/japan/experiential-stays/'],
  ['/themes/geisha', '/japan/geisha/'],
  ['/themes/kintsugi', '/japan/kintsugi/'],
  ['/themes/kintsugi_', '/japan/kintsugi/'],
  ['/themes/sake-and-alcohol-tastings', '/japan/sake-and-alcohol-tastings/'],
  ['/themes/saketasting', '/japan/sake-and-alcohol-tastings/'],
  ['/themes/sumo', '/japan/sumo/'],
  ['/themes/tea-ceremony', '/japan/tea-ceremony/'],
  ['/themes/tea_ceremony', '/japan/tea-ceremony/'],
  ['/themes/temple_shrine', '/japan/temples-and-shrines/'],
  ['/themes/traditionalmusic_art', '/japan/traditional-music/'],
  ['/themes/ukiyo-e-printing', '/japan/ukiyo-e-printing/'],
  ['/themes/zen-meditation', '/japan/zen-meditation/'],
  ['/themes/zenexperience', '/japan/zen-meditation/'],
  ['/areas/hidatakayama', '/hida-takayama/'],
  ['/areas/hiroshima', '/hiroshima/'],
  ['/areas/hiroshima_', '/hiroshima/'],
  ['/areas/kanazawa-hokuriku', '/kanazawa/'],
  ['/areas/setouchi', '/naoshima-islands/'],
  ['/areas/western-tokyo', '/tokyo/'],
  ['/categories/osakaexpo', '/osaka/'],
  ['/categories/teaceremony', '/japan/tea-ceremony/'],
]);

// 同ワークリストの P1「301解除して直接404」「現状404を維持」。受け皿が無いので、正規化や /japan/{slug}/ への 301 を挟むと 301 → 404 になる
const GONE_PATHS = new Set([
  '/themes/kyoto_cherry_blossoms',
  '/themes/tokyo_cherry_blossoms',
  '/categories/0iv9qb-wvs1r',
  '/categories/0lc9usqfmj-4',
  '/categories/0x0q-cfrfvp',
  '/categories/2v56wjv__e9s',
  '/categories/8_3jnaf6dc',
  '/categories/al7gfn3so5',
  '/categories/cd4vtgd8p7zq',
  '/categories/dy0d4bs6-xhc',
  '/categories/fc052n4jh1j',
  '/categories/g4elmu9fyj',
  '/categories/hi1r61l_hax',
  '/categories/tvwtwj-lypo',
  '/categories/xghb11i93g57',
  '/categories/yiusdfqpcian',
  '/categories/ywc533cca',
]);

const withoutTrailingSlash = (pathname: string): string => pathname.replace(/\/$/, '');

const resolveLegacyRedirect = (pathname: string): LegacyRedirect | null => {
  if (pathname.startsWith('/about')) {
    return { path: ABOUT_LP_URL, keepQuery: false };
  }
  if (pathname === '/experiences' || pathname === '/experiences/') {
    return { path: EXPERIENCE_SEARCH_PATH, keepQuery: true };
  }
  if (pathname === '/partner' || pathname === '/partner/') {
    return { path: '/contact/', keepQuery: false, query: { partner: 'true' } };
  }

  const retiredPageDestination = RETIRED_PAGE_REDIRECTS.get(withoutTrailingSlash(pathname));
  if (retiredPageDestination) {
    return { path: retiredPageDestination, keepQuery: true };
  }

  const segments = withoutTrailingSlash(pathname).split('/').filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  // /categories/[slug] は廃止済みのカテゴリーページ。`/themes/` も廃止予定（JCAT-5903）なので
  // 現行のテーマページ `/japan/[theme]` へ直接着地させ、`/themes/` の削除順に依存しないようにする。
  if (segments[0] === 'categories' && segments.length === 2) {
    const result = resolveTheme(segments[1]);
    if (result?.type === 'group') {
      return { path: EXPERIENCE_SEARCH_PATH, keepQuery: true, query: { themes: result.themes } };
    }
    return { path: `/japan/${result?.newTheme ?? segments[1]}/`, keepQuery: true };
  }

  // /themes/[id] の旧テーマ名。`/categories/` と同じ理由で、廃止予定の `/themes/` を経由させず現行のテーマページへ着地させる
  if (segments[0] === 'themes' && segments.length === 2) {
    const result = resolveTheme(segments[1]);
    if (!result) return null;
    if (result.type === 'simple') {
      return { path: `/japan/${result.newTheme}/`, keepQuery: true };
    }
    return { path: EXPERIENCE_SEARCH_PATH, keepQuery: true, query: { themes: result.themes } };
  }

  // /areas/[id] の旧地名。同上、廃止予定の `/areas/` ではなく現行のエリアページへ着地させる
  if (segments[0] === 'areas' && segments.length === 2) {
    const newArea = resolveDestination(segments[1]);
    if (!newArea) return null;
    return { path: `/${newArea}/`, keepQuery: true };
  }

  // /japan/[theme]
  if (segments[0] === 'japan' && segments.length === 2) {
    const result = resolveTheme(segments[1]);
    if (!result) return null;
    if (result.type === 'simple') {
      return { path: `/japan/${result.newTheme}/`, keepQuery: true };
    }
    return { path: EXPERIENCE_SEARCH_PATH, keepQuery: true, query: { themes: result.themes } };
  }

  // 有効なエリアでなければスキップ
  if (!validDestinations.has(segments[0])) {
    return null;
  }

  // /[area]
  if (segments.length === 1) {
    const newArea = resolveDestination(segments[0]);
    if (!newArea) return null;
    return { path: `/${newArea}/`, keepQuery: true };
  }

  // /[area]/[theme]
  if (segments.length === 2) {
    const newArea = resolveDestination(segments[0]);
    const themeResult = resolveTheme(segments[1]);

    // どちらも変換不要ならスキップ
    if (!newArea && !themeResult) return null;

    const finalArea = newArea ?? segments[0];

    if (!themeResult || themeResult.type === 'simple') {
      const finalTheme = themeResult ? themeResult.newTheme : segments[1];
      return { path: `/${finalArea}/${finalTheme}/`, keepQuery: true };
    }

    // グループリダイレクト: /s/experiences/[area]/?themes=...
    return { path: `${EXPERIENCE_SEARCH_PATH}${finalArea}/`, keepQuery: true, query: { themes: themeResult.themes } };
  }

  return null;
};

/** overrides が無いときはクエリ文字列をそのまま繋ぐ（`,` などが再エンコードされて別 URL になるのを避ける） */
const buildLocation = (path: string, search: string, overrides?: Record<string, string>): string => {
  if (!overrides) {
    return search ? `${path}?${search}` : path;
  }
  const params = new URLSearchParams(search);
  Object.entries(overrides).forEach(([key, value]) => params.set(key, value));
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

/**
 * リクエスト URL（パス + クエリ + ハッシュ）から 301 の遷移先を求める。リダイレクト不要なら null。
 *
 * ハッシュはクライアント遷移（`to.fullPath`）でのみ届く。切り離さずに正規化すると `/#areas` が
 * `/#areas/` になり、`plugins/scrollToHash.ts` の `querySelector('#areas/')` が無効セレクタで落ちる。
 *
 * @param rawUrl `/kyoto?foo=bar#access` のようなパス + クエリ + ハッシュ
 */
export const resolveRedirectLocation = (rawUrl: string): string | null => {
  const hashIndex = rawUrl.indexOf('#');
  const hash = hashIndex === -1 ? '' : rawUrl.slice(hashIndex);
  const urlWithoutHash = hashIndex === -1 ? rawUrl : rawUrl.slice(0, hashIndex);

  const queryIndex = urlWithoutHash.indexOf('?');
  const pathname = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const search = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);

  if (!isNormalizationTarget(pathname) || GONE_PATHS.has(withoutTrailingSlash(pathname))) {
    return null;
  }

  const legacy = resolveLegacyRedirect(pathname);
  const location = legacy
    ? buildLocation(legacy.path, legacy.keepQuery ? search : '', legacy.query)
    : pathname.endsWith('/')
      ? null
      : buildLocation(`${pathname}/`, search);

  if (location === null) {
    return null;
  }
  const withHash = `${location}${hash}`;
  return withHash === rawUrl ? null : withHash;
};
